import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { RoutesEnum } from '../lib/routes'
import { useLang } from '../lib/useLang'

export default function Nav({ frozen = false, userEmail = '', onLogout }) {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const navigate = useNavigate()
  const loc = useLocation()
  const { t, lang, setLang } = useLang()

  useEffect(() => {
    if (frozen) return
    const onScroll = () => setScrolled(window.scrollY > 80)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [frozen])

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const links = [
    { label: t('nav.plans') || 'Pricing', to: RoutesEnum.PLANS },
    { label: t('nav.contactUs') || 'Contact', to: RoutesEnum.CONTACT },
    { label: t('nav.research') || 'Research', href: RoutesEnum.RESEARCH },
    { label: t('nav.privacyPolicy') || 'Privacy Policy', to: RoutesEnum.PRIVACY },
  ]

  const letters = ['M', 'O', 'D', 'E', 'L', 'L', 'E', 'R']
  const lastIdx = letters.length - 1

  return (
    <div className={`nav-wrap ${scrolled ? 'scrolled' : ''}`}>
      <div className='relative pl-8 pr-6 sm:pl-14 sm:pr-10 lg:pl-20 lg:pr-14 h-[64px] flex items-center justify-between gap-4'>
        <Link to={RoutesEnum.ROOT} className='brand' aria-label='Modeller home'>
          <span className='brand-mark'>
            {letters.map((l, i) => {
              const persistent = i === 0 || i === lastIdx
              return (
                <span
                  key={i}
                  className={`brand-letter ${persistent ? 'persistent' : 'middle'}`}
                  style={{
                    transitionDelay: scrolled
                      ? `${(lastIdx - i) * 28}ms`
                      : `${i * 24}ms`,
                  }}
                >
                  {l}
                </span>
              )
            })}
          </span>
        </Link>

        {/* Центрируем всю группу ссылок как единый блок по центру страницы.
            Так раскладка остаётся сбалансированной при любой длине лейблов
            (RU длиннее EN) — раньше средний пункт пиннился в центр и группа
            выглядела перекошенной. */}
        <nav
          className='hidden md:flex items-center justify-center pointer-events-none'
          style={{
            position: 'absolute',
            left: '50vw',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            columnGap: '28px',
          }}
        >
          {links.map((l) => l.href ? (
            <a key={l.href} href={l.href} className='nav-link pointer-events-auto whitespace-nowrap text-center' style={{ lineHeight: 1.2 }}>
              {l.label}
            </a>
          ) : (
            <Link
              key={l.to}
              to={l.to}
              className='nav-link pointer-events-auto whitespace-nowrap text-center'
              style={{
                lineHeight: 1.2,
                ...(loc.pathname === l.to ? { color: 'var(--ink)' } : null),
              }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className='flex items-center gap-3'>
          {/* Language switcher */}
          <div className='lang-switch' role='group' aria-label='Language'>
            {['en', 'ru'].map((l) => (
              <button
                key={l}
                type='button'
                onClick={() => setLang(l)}
                className={`lang-switch-btn ${lang === l ? 'is-active' : ''}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {userEmail ? (
            <div className='relative' ref={menuRef}>
              <button
                type='button'
                onClick={() => setMenuOpen((s) => !s)}
                className='user-chip'
                aria-expanded={menuOpen}
              >
                <span className='user-chip__avatar'>
                  {(userEmail || '?').slice(0, 1).toUpperCase()}
                </span>
                <span className='user-chip__email'>{userEmail}</span>
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  className='user-chip__chevron'
                  style={{ transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path d='M6 9l6 6 6-6' />
                </svg>
              </button>
              {menuOpen && (
                <div className='user-menu' role='menu'>
                  <div className='user-menu__head'>
                    <div className='user-menu__avatar'>
                      {(userEmail || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className='user-menu__head-info'>
                      <div className='user-menu__label'>{t('nav.signedInAs') || 'Signed in as'}</div>
                      <div className='user-menu__email' title={userEmail}>{userEmail}</div>
                    </div>
                  </div>

                  <div className='user-menu__group'>
                    <button
                      type='button'
                      className='user-menu__item'
                      onClick={() => { setMenuOpen(false); navigate(RoutesEnum.APP) }}
                    >
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6'>
                        <path d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' />
                      </svg>
                      <span>{t('nav.myProjects') || 'My Projects'}</span>
                    </button>
                    <button
                      type='button'
                      className='user-menu__item'
                      onClick={() => { setMenuOpen(false); navigate(RoutesEnum.SETTINGS) }}
                    >
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6'>
                        <circle cx='12' cy='12' r='3' />
                        <path d='M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z' />
                      </svg>
                      <span>{t('nav.settings') || 'Settings'}</span>
                    </button>
                    <button
                      type='button'
                      className='user-menu__item'
                      onClick={() => { setMenuOpen(false); navigate(RoutesEnum.INTEGRATIONS) }}
                    >
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6'>
                        <path d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' />
                      </svg>
                      <span>{t('nav.integrations')}</span>
                    </button>
                  </div>

                  <div className='user-menu__group user-menu__group--last'>
                    <button
                      type='button'
                      className='user-menu__item user-menu__item--danger'
                      onClick={() => { setMenuOpen(false); onLogout && onLogout() }}
                    >
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6'>
                        <path d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1' />
                      </svg>
                      <span>{t('nav.signOut') || 'Sign Out'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to={RoutesEnum.AUTH} className='nav-link hidden sm:inline'>
                {t('nav.signIn') || 'Log In'}
              </Link>
              <Link to={RoutesEnum.CONTACT} className='btn-primary btn-small'>
                {t('nav.getStarted') || 'Write to us'}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
